/**
 * Public referral landing page.
 * No auth required. Loads tenant branding, displays referrer name, allows
 * the new visitor to sign up.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Gift, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { useApplyTenantBranding } from "@/contexts/branding";
import { API, formatApiError } from "@/lib/api";

export default function PublicReferral() {
  const { slug, code } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/referral/${slug}/${code}`);
        setInfo(data);
      } catch (e) {
        setErr("Código de indicação inválido ou empresa indisponível.");
      } finally { setLoading(false); }
    })();
  }, [slug, code]);

  useApplyTenantBranding(info?.tenant?.branding);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/public/referral/${slug}/${code}`, form);
      setDone(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  if (err && !info) return (
    <div className="min-h-screen grid place-items-center p-6">
      <Card className="p-8 rounded-2xl max-w-md text-center">
        <div className="text-destructive font-display text-xl mb-2">Ops…</div>
        <div className="text-sm text-muted-foreground">{err}</div>
        <Link to="/" className="inline-flex items-center gap-1.5 mt-6 text-sm underline"><ArrowLeft className="w-3 h-3" /> Página inicial</Link>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden text-tenant-foreground"
        style={{ background: `hsl(var(--tenant-primary))` }}>
        <div className="absolute inset-0 swiss-grid opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white/15 grid place-items-center font-display font-bold text-xl">
            {(info?.tenant?.branding?.display_name || info?.tenant?.legal_name || "·").charAt(0).toUpperCase()}
          </div>
          <div className="font-display font-bold text-xl">{info?.tenant?.branding?.display_name || info?.tenant?.legal_name}</div>
        </div>
        <div className="relative max-w-md space-y-4">
          <div className="text-xs uppercase tracking-[0.25em] opacity-70 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Você foi indicado por
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight">{info?.referrer_name}</h1>
          <p className="text-base opacity-90 leading-relaxed">
            Cadastre-se agora e receba um <strong>brinde de boas-vindas</strong>. Quem te indicou também ganha pontos.
          </p>
        </div>
        <div className="relative text-xs opacity-60">Código <span className="font-mono">{code}</span></div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        {done ? (
          <Card className="w-full max-w-md p-8 rounded-tenant border-border text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Pronto, {form.name}!</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Sua indicação foi registrada para {done.tenant?.display_name || done.tenant?.slug}.
              Em breve a equipe entrará em contato.
            </p>
            <div className="inline-flex items-center gap-1.5 text-sm font-medium text-tenant">
              <Gift className="w-4 h-4" /> Bem-vindo!
            </div>
          </Card>
        ) : (
          <Card className="w-full max-w-md p-8 rounded-tenant border-border">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Cadastro rápido</div>
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-1">Aceite a indicação</h2>
            <p className="text-sm text-muted-foreground mb-6">
              {info?.referrer_name} acredita que você vai adorar. Em 1 minuto você está dentro.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Seu nome *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>WhatsApp</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+55 11 90000-0000" /></div>
              {err && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">{err}</div>}
              <Button type="submit" disabled={busy} className="w-full h-11 gap-2"
                style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                Aceitar indicação
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
